import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Box, FormHelperText } from '@mui/material';
import JoditEditor from 'jodit-react';
import { base64toBlob, extractTextFromHTML } from '@shared/commonFunctions';
import { useDebounce } from '@shared/hooks/useDebounce';
import { Notistack } from './Notistack/Notistack';

export interface ITextEditorProps {
  errorMessage?: string;
  label?: string;
  onChange?: (html: string, plainText: string) => void;
  text: string;
  withPlaceholder?: string;
  disable?: boolean;
}

const Wrapper = styled.div`
  position: relative;
  .jodit-container:not(.jodit_inline).jodit_disabled {
    background: transparent;
  }
  .jodit-container:not(.jodit_inline).jodit_disabled .jodit-workplace {
    opacity: 1;
  }
`;

const ErrorMessageWrapper = styled.div`
  position: relative;
  top: -15px;
`;

const TextEditorLabel = styled.div`
  z-index: 1;
  color: #000;
  font-size: 11px;
  font-weight: 600;
  margin-bottom : 10px;
  padding-left: 10px;
`;

const config = {
  iframe: true,
  autofocus: false,
  disablePlugins: 'ai-assistant,about,add-new-line',
  statusbar: false,
  height: 550,
  uploader: {
    insertImageAsBase64URI: true,
  },
};

export const TextEditor = ({ errorMessage, label, onChange, text, withPlaceholder = '', disable }: ITextEditorProps) => {
  const [content, setContent] = useState('');
  const debouncedInputValue = useDebounce(content, 1000);

  const editorConfig = useMemo(
    () => ({ ...config, placeholder: withPlaceholder, disabled: disable, toolbar: !disable }),
    [withPlaceholder]
  );

  useEffect(() => {
    if (text) setContent(text);
    else setContent('');
  }, [text]);

  const handleEditorChange = (newContent: string) => {
    const element = document.createElement('html');
    element.innerHTML = newContent;
    const imgs = element.getElementsByTagName('img');
    let updatedContent = newContent;

    for (const img of imgs) {
      if (img?.src.startsWith('data:image')) {
        const file = base64toBlob(img.src);
        const fileSize = file?.size;
        const sizeInKB = (fileSize ? fileSize : 0) / 1024;

        if (sizeInKB > 700) {
          Notistack.generalError('Uploaded image should be less than 700KB');
          img.remove();
          updatedContent = element.innerHTML;
        }
      }
    }

    return updatedContent;
  };

  const handleChange = useCallback(
    (newContent: string) => {
      const updatedContent = handleEditorChange(newContent);
      const plainText = extractTextFromHTML(updatedContent);
      onChange && onChange(updatedContent, plainText);
      setContent(updatedContent);
    },
    [handleEditorChange]
  );

  return (
    <>
      {label && <TextEditorLabel>{label}</TextEditorLabel>}

      <Wrapper>
        <Box style={{ border: errorMessage ? '1px solid rgb(211, 47, 47)' : '1px solid #C4C4C4', borderRadius: '8px' }}>
          <JoditEditor
            config={editorConfig}
            value={debouncedInputValue}
            onChange={handleChange}
          />
        </Box>
      </Wrapper>

      {errorMessage && (
        <ErrorMessageWrapper>
          <FormHelperText error={!!errorMessage}>{errorMessage}</FormHelperText>
        </ErrorMessageWrapper>
      )}
    </>
  );
};
